<script setup lang="ts">
import { useIosDevice } from "@/composables/useIosDevice";
const { isIOS } = useIosDevice();
const { isMobile } = useDevice();
import { useColorMode } from "#imports";

const WrAnimations = defineAsyncComponent(() => import("./WrAnimations.vue"));
const colorMode = useColorMode();
</script>

<template>
  <div class="w-full max-w-4xl flex flex-col text-[var(--foreground)]">
    <!-- Pipeline Flow -->
    <div
      class="w-full flex flex-col items-center lg:flex-row lg:items-start relative gap-20 lg:gap-10"
    >
      <!-- Build Job -->
      <div class="lg:w-1/3">
        <div class="text-center mb-4">Build</div>
        <div
          class="*:w-full flex justify-center relative after:content-none lg:after:content-[''] after:absolute after:border-t-2 after:border-[var(--color-deployment-border)] after:border-dashed after:w-10 after:-right-10 after:top-1/2"
        >
          <span
            class="hidden lg:block absolute top-1/2 -right-5 h-full w-10 rounded-br-none border-[var(--color-deployment-border)] border-dashed border-r-2 border-b-none rounded-3xl"
          >
          </span>
          <span
            class="absolute top-full right-1/2 h-[58px] border-r border-[var(--color-deployment-border)] border-dashed rounded-3xl rounded-tr-none lg:hidden"
          ></span>
          <span
            class="absolute top-full left-1/2 h-[58px] border-l border-[var(--color-deployment-border)] border-dashed rounded-3xl rounded-tl-none lg:hidden"
          ></span>
          <div
            class="border border-[var(--color-deployment-border)] rounded-full px-5 py-3 lg:py-3.5 flex items-center justify-between space-x-3"
          >
            <div class="flex gap-2 items-center">
              <div class="flex items-center justify-center relative w-8">
                <div
                  class="rounded-full absolute top-0 -translate-y-1/2 border-2 size-7 left-1/2 -translate-x-1/2 border-[var(--color-deployment-border)] dark:border-[#cccdcd]"
                ></div>
                <video
                  autoplay
                  loop
                  muted
                  playsinline
                  preload="none"
                  class="absolute left-0 rounded-full"
                  :width="isMobile ? 32 : 34"
                  :height="isMobile ? 32 : 34"
                >
                  <!-- Safari iOS/macOS -->
                  <source
                    v-if="isIOS"
                    src="/images/check-index.hevc.mp4"
                    type='video/mp4; codecs="hvc1"'
                  />
                  <source
                    v-else
                    src="/images/check-index.webm"
                    type="video/webm"
                  />
                </video>
              </div>
              <span class="max-sm:text-sm">build-job</span>
            </div>
            <div
              class="size-7 lg:size-8 rounded-full border-2 border-[var(--color-deployment-border)] dark:border-[#cccdcd] flex items-center justify-center"
            >
              <video
                v-if="isIOS"
                autoplay
                loop
                muted
                playsinline
                preload="none"
                width="22"
                height="22"
                class="size-4 lg:size-[22px] rounded-full"
              >
                <source
                  :src="
                    colorMode.preference == 'dark'
                      ? '/images/clockwise-dark.hevc.mp4'
                      : '/images/clockwise.hevc.mp4'
                  "
                  type='video/mp4; codecs="hvc1"'
                />
              </video>

              <video
                v-else
                autoplay
                loop
                muted
                playsinline
                preload="none"
                width="22"
                height="22"
                class="size-4 lg:size-[22px] rounded-full"
              >
                <source
                  :src="
                    colorMode.preference == 'dark'
                      ? '/images/clockwise-dark.webm'
                      : '/images/clockwise.webm'
                  "
                  type="video/webm"
                />
              </video>
            </div>
          </div>
        </div>
      </div>

      <!-- Test Jobs Container with vertical spacing -->
      <div class="lg:w-1/3">
        <div class="text-center mb-4">Test</div>
        <div
          class="w-full *:w-full flex flex-row lg:flex-col items-center justify-between lg:space-y-8 max-lg:gap-4"
        >
          <!-- Test Job 1 -->
          <div
            class="border border-[var(--color-deployment-border)] rounded-full px-4 py-3 flex items-center justify-between space-x-3 right relative"
          >
            <span
              class="absolute bottom-full left-[100px] h-16 border-l-2 border-[var(--color-deployment-border)] border-dashed rounded-3xl w-full rounded-bl-none lg:hidden"
            ></span>
            <span
              class="absolute top-full left-[100px] h-16 border-l-2 border-[var(--color-deployment-border)] border-dashed rounded-3xl w-full rounded-tl-none lg:hidden !ml-0"
            ></span>
            <div class="flex items-center gap-3 !ml-0">
              <div
                class="rounded-full p-px grid place-content-center border border-gray-500"
              >
                <WrAnimations />
              </div>
              <span class="max-sm:text-sm">test-job1</span>
            </div>
            <div
              class="hidden size-6 lg:size-8 rounded-full border-2 border-[var(--color-deployment-border)] dark:border-[#cccdcd] lg:flex items-center justify-center"
            >
              <WrSVGIcon name="WrStop" class="size-4 text-[#7d7d7d]" />
            </div>
          </div>

          <!-- Test Job 2 -->
          <div
            class="border border-[var(--color-deployment-border)] rounded-full px-4 py-3 flex items-center justify-between space-x-3 relative"
          >
            <span
              class="absolute bottom-full right-[100px] h-16 border-r-2 border-[var(--color-deployment-border)] border-dashed rounded-3xl w-full rounded-br-none lg:hidden"
            ></span>
            <span
              class="absolute top-full right-[100px] h-16 border-r-2 border-[var(--color-deployment-border)] border-dashed rounded-3xl w-full rounded-tr-none lg:hidden"
            ></span>

            <span
              class="hidden lg:block absolute top-0 -left-[22px] h-1/2 !w-6 rounded-tl-none border-[var(--color-deployment-border)] border-dashed border-l-2 border-b-none rounded-3xl after:content-['>'] text-xs after:absolute after:-bottom-1.5 after:right-2 !ml-0"
            >
            </span>
            <span
              class="hidden lg:block absolute -top-1/2 -right-5 h-full !w-10 rounded-tr-none border-[var(--color-deployment-border)] border-dashed border-r-2 border-b-none rounded-3xl"
            >
            </span>

            <div class="flex items-center gap-3 !ml-0">
              <div
                class="rounded-full p-px grid place-content-center border border-gray-500"
              >
                <WrAnimations />
              </div>
              <span class="max-sm:text-sm">test-job2</span>
            </div>
            <div
              class="hidden size-8 rounded-full border-2 border-[var(--color-deployment-border)] dark:border-[#cccdcd] lg:flex items-center justify-center"
            >
              <WrSVGIcon name="WrStop" class="size-4 text-[#7d7d7d]" />
            </div>
          </div>
        </div>
      </div>

      <!-- Deploy Job -->
      <div class="lg:w-1/3 max-lg:mt-10">
        <div class="text-center mb-4 hidden lg:block">Deploy</div>
        <div
          class="*:w-full flex justify-center relative after:content-none lg:after:content-[''] after:absolute after:border-t-2 after:border-[var(--color-deployment-border)] after:border-dashed after:w-10 after:-left-10 after:top-1/2 after:-translate-y-1/2"
        >
          <span
            class="hidden lg:block absolute top-1/2 -left-[22px] h-1/2 !w-6 rounded-bl-none border-[var(--color-deployment-border)] border-dashed border-l-2 border-b-none rounded-3xl after:content-['>'] text-xs after:absolute after:-top-2.5 after:right-0"
          >
          </span>

          <span
            class="absolute bottom-full right-1/2 h-[58px] border-r-2 border-[var(--color-deployment-border)] border-dashed rounded-3xl rounded-br-none lg:hidden"
          ></span>
          <span
            class="absolute bottom-full left-1/2 h-[58px] border-l-2 border-[var(--color-deployment-border)] border-dashed rounded-3xl rounded-bl-none lg:hidden"
          ></span>

          <div
            class="border border-[var(--color-deployment-border)] rounded-full px-4 py-3 flex items-center justify-between space-x-3"
          >
            <div class="flex items-center gap-3">
              <div
                class="size-[28px] lg:size-[36px] rounded-full p-px grid place-content-center border-2 border-[var(--color-deployment-border)] dark:border-[#cccdcd]"
              >
                <!-- <NuxtImg alt="circle-zoom" loading="lazy" src="/images/circle-zoom.webm" width="68" height="68" class="size-[50px] lg:size-[68px] absolute top-0.5 left-1.5 lg:-top-[3px] lg:left-[1px]" /> -->
                <div
                  class="size-[22px] rounded-full bg-[var(--foreground)] animate-[zoom_1s_infinite]"
                ></div>
              </div>
              <span>deploy-prod</span>
            </div>
            <div
              class="size-6 lg:size-8 rounded-full border-2 border-[var(--color-deployment-border)] dark:border-[#cccdcd] flex items-center justify-center"
            ></div>
          </div>
        </div>
        <div class="text-center mt-4 lg:hidden block">Deploy</div>
      </div>

      <!-- Connector Lines -->
      <!-- <div
        class="absolute top-0 left-0 h-16 pointer-events-none border border-[var(--color-deployment-border)] border-dashed w-[48%] left-[49%] -translate-x-1/2 rounded-b-xl top-6">
      </div> -->
    </div>
  </div>
</template>
